import _ from 'lodash'
import Backbone from 'backbone'
import template from './data-source-type-chooser.hbs'
import i18n from 'scoreboard/components/i18n'
import * as DataSourceTypes from 'scoreboard/components/import-data-source/import-data-source-types'
import FormView from 'scoreboard/components/form-view'
import constants from 'scoreboard/components/constants'
const { REST, SOAP } = constants
import TilesWithIconOptions from 'scoreboard/components/tiles-with-icon-options'
import WsConstants from 'scoreboard/admin/model/ws-constants'
const { WS_VENDOR_TYPE  } = WsConstants

const DataSourceTypeChooser = Backbone.View.extend({

	tagName: 'form',

	className: 'data-source-type-chooser',

	events: {
		'click .tiles li': 'onDataSourceTypeSelected'
	},

	/**
	 * Form for choosing a data source type. Usually part of a wizard.
	 * Used when creating datasets, value imports and scorecard item imports
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 * @param {Import|Dataset} options.model
	 * @param {String} [options.title] optional title for the dialog
	 */
	initialize: function(options) {
		this.options = options || {}
		this.formView = new FormView({
			el: this.el,
			allowCleanSubmission: () => _.isNil(this.test()),
			test: this.test.bind(this),
			submit: this.submit.bind(this)
		})
		this.menuOptions = DataSourceTypes.menuOptions(this.model)
		// By default, the upload spreadsheet option should be selected
		let selectedType = 'upload'
		let dataSourceFromMetaData = this.model.getDataSourceMetadata('source')
		if (!_.isNil(dataSourceFromMetaData)) {
			if (this.model.isSpreadsheet()) {
				selectedType = dataSourceFromMetaData
			} else if (this.model.isDatabase()) {
				// the db product is not available in the DataSourceMetadata,
				// but it is available in the retrieved connections
				let connectionById = _.keyBy(this.options.connections, 'id')
				let connectionId = this.model.getDataSourceMetadata('database.connectionId')
				// #53861 use the current import connection, if available, in case
				// the list of connections is empty because the user does not have permissions
				// for any import connections
				let currentConnection = _.get(this.options, 'currentConnection')
				let connection = currentConnection || connectionById[connectionId]
				selectedType = _.get(connection, 'product')
				// if we are moving back and forth in a wizard and did not
				// select a valid connection, used the db product held in the
				// wizard options
				if (_.isNil(selectedType)) {
					let dbProductFromWizard =
						_.get(this.options, 'dataSourceTypeObj.type')
					selectedType = dbProductFromWizard
				}
			} else if (_.includes([REST, SOAP], dataSourceFromMetaData)) {
				selectedType = dataSourceFromMetaData
				const vendorType = this.options?.currentConnection?.wsVendorType
				if (vendorType === WS_VENDOR_TYPE.ORACLE_NETSUITE) {
					selectedType = WS_VENDOR_TYPE.ORACLE_NETSUITE
				}
			} else {
				throw new Error('New flow for selecting datasource type not yet implemented for this option')
			}
		} else {
			// if this is an existing dataset with 'None' as a datasource,
			// the dataSourceMetadata.source will be null, so we want 'none'
			// to be the selected type in this case
			if (!this.model.isNew() && this.model.get('type') === 'dataset') {
				selectedType = 'none'
			}
		}
		this.updateSelectionState(selectedType)
	},

	render: function() {
		this.$el.empty().append(template(this.context()))
		this.renderMenuOptions()
		if (this.options.wizard) {
			this.options.wizard.render({
				formView: this.formView,
				el: this.el,
				stateSaver: this.saveState.bind(this)
			})
		}
		this.formView.start()
		return this
	},

	renderMenuOptions: function() {
		const dataSourceOptions = new TilesWithIconOptions({
			defaultValue: this.selectedDataSourceType.type,
			tooltipFn: (target) => {
				DataSourceTypes.displayTooltipIfNotImplemented(target,
					this.model, this.menuOptions)
			},
			sections: this.menuOptions,
		}).render()
		dataSourceOptions.on('option-selected', (selectedType) => {
			const currentGroup = this.selectedDataSourceType.groupType
			this.updateSelectionState(selectedType)
			let selectedGroup = this.selectedDataSourceType.groupType
			// #53214 immediately display any error message due to selection
			this.formView.validate()
			// update the wizard's views if changing group type of data source
			// (eg from an 'upload' spreadsheet data source to a 'my sql' db data source)
			if (selectedGroup != currentGroup) {
				_.invoke(this.options.wizard, 'changeViews', this.selectedDataSourceType)
			}
		})
		this.$('.data-source-options').append(dataSourceOptions.el)
	},

	updateSelectionState(selectedType) {
		this.selectedDataSourceType =
			DataSourceTypes.byType(selectedType, this.model, this.menuOptions)
	},

	context: function() {
		let titleKey = this.model.isNew() ?
			'admin.import.datasource.type.choose' : 'admin.import.datasource.type.edit'
		let title
		if (!_.isNil(this.options.title)) {
			title = this.options.title
		} else {
			let typeKey = this.model.get('type') === 'dataset'
				? 'datasets.dataset'
				: _.get(this.options, 'labels.singular', 'admin.import')
			title = i18n.get(titleKey, typeKey)
		}
		return {
			title
		}
	},

	test: function () {
		if (!this.model.isNew() && this.model.get('type') === 'dataset') {
			// #53214 prevent changing dataset data source type to database
			// without a record ID already having been set
			let primaryKeyNeeded =
				this.selectedDataSourceType?.groupType == 'database' &&
				!this.model.hasPrimaryKeyField()
			if (primaryKeyNeeded) {
				return i18n.get('datasets.dataSource.database.primaryKey.required')
			}
		}
		return FormView.prototype.test.apply(this, arguments)
	},

	saveState: function() {
		// set the dataSourceMetadata.source to let the wizard know what the
		// next view should be
		let dataSourceMetadata = this.model.getDataSourceMetadata()
		let { groupType, type } = this.selectedDataSourceType || {}
		if (groupType === 'spreadsheet') {
			// for spreadsheet sources, the 'source' will be the type
			dataSourceMetadata.source = type
		} else {
			// for db sources, the 'source' will be the groupType (ex 'database')
			dataSourceMetadata.source = groupType

			if (this.selectedDataSourceType?.type === WS_VENDOR_TYPE.ORACLE_NETSUITE) {
				dataSourceMetadata.subType = this.selectedDataSourceType?.type
			} else {
				// #53720 reset the subtype and rest metadata when selected data source type is REST
				dataSourceMetadata.subType = null
			}
		}
	},

	submit: function(e) {
		e.preventDefault()
		this.saveState()
		if (this.options.wizard) {
			// #44888 indicator for subsequent wizard steps that we
			// are using the new flow for choosing import data source
			this.options.wizard.options.dataSourceTypeChosen = true
			this.options.wizard.options.dataSourceTypeObj = this.selectedDataSourceType
			if (this.options.wizard.isLastStep()) {
				this.options.wizard.finish(this.model)
				this.trigger('done', this.model)
			} else {
				this.options.wizard.next()
			}
		}
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([ this.tooltip ], 'remove')
	}

})

export default DataSourceTypeChooser
DataSourceTypeChooser.__filename__ = 'components/import-data-source/data-source-type-chooser.js'
